import React, { useEffect, useState } from 'react'
import { withStyles } from '@mui/styles'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { Button, Typography } from '@mui/material'
import { muiOptions, MuiProps } from '../../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../../infrastructure/i18nextHelper'
import { hasFeature, hasVesselDealDrivenFeature } from '../../../../infrastructure/feature'
import { Select, CustomDialog, TextField } from '../../../common/customComponents'
import { VesselEditContainer } from '../vesselEditStore'
import { PurchaseMovement, MovementType, SitePurchaseMovement, VesselProduct } from '../../vesselModels'
import { commonStyle, TotalDispatchedQuantity, Unbalances } from './_common'
import { SelectAssignableDeals, ConfirmDealAssignation } from '../../../common/assign/assignDealPopup'
import { PriceSeveralMovementTypesTable } from './priceSeveralMovementTypesTable'
import { PricePurchaseMovementTable } from './pricePurchaseMovementTable'
import { Item } from '../../../common/components/select/types'

let tBase = 'vessels.label.purchaseMovement.'

export type PurchaseMovementTableProps = {
    vesselProduct: VesselProduct
    purchaseMovement: PurchaseMovement
    canDeletePurchaseMovement: boolean
    isFixedPrice: boolean
    addSite: (purchaseMvt: PurchaseMovement, defaultSitePurchaseMovement: SitePurchaseMovement) => void
    createSite: (company: string, dutyStatus: string) => SitePurchaseMovement
    duplicateSite: (site: SitePurchaseMovement) => SitePurchaseMovement
    setPurchaseMovementToDelete: () => void
    setSitePurchaseMovementToDelete: (site: SitePurchaseMovement) => void
}

function _PriceMovementContainer(props: PurchaseMovementTableProps & MuiProps) {
    let { classes, vesselProduct, purchaseMovement, canDeletePurchaseMovement, isFixedPrice,
        createSite, duplicateSite, addSite, setPurchaseMovementToDelete, setSitePurchaseMovementToDelete } = props

    let [selectedSite, setSelectedSite] = useState<string>(purchaseMovement.sites[0]?.siteCode ?? '')
    let vessel = VesselEditContainer.useContainer()

    let hasOnlyOneCompany = vessel.movementStock.map(x => x.company).distinct().length == 1
    let hasOnlyOneDutyStatus = vessel.movementStock.map(x => x.dutyStatus).distinct().length == 1

    let canHaveSeveralMovementTypes = hasFeature("SalesAndTransfersInVessels")
        && hasOnlyOneCompany
        && hasOnlyOneDutyStatus

    let sites = (purchaseMvt: PurchaseMovement): Item[] => {
        if (!vessel.movementStock || !vessel.sites)
            return []

        let purchaseMovementStockSites = vessel.movementStock
            .filter(x => x.company === purchaseMvt.companyCode
                && x.dutyStatus === purchaseMvt.dutyStatus
                && x.productId === vesselProduct.productId)
            .map(x => x.site)
        return vessel.sites
            .filter(x => purchaseMovementStockSites.includes(x.code))
            .map(x => ({ value: x.code, text: x.name }))
    }

    let addNewMovement = () => {
        let site = createSite(purchaseMovement.companyCode, purchaseMovement.dutyStatus)
        if (purchaseMovement.type === "FixedPrice")
            site = vessel.applyFixedPrice(vesselProduct.id, site)
        if (canHaveSeveralMovementTypes)
            site.siteCode = selectedSite
        addSite(purchaseMovement, site)
    }

    let mainUnbalance = () => {
        return purchaseMovement.sites.map(x => x.movementType == MovementType.PurchaseType
            ? x.quantity ?? 0
            : -(x.quantity ?? 0)
        ).reduce((prev, curr) => curr = curr + prev, 0)
    }

    //note: This computation of the unbalance must match the one made at backend in PurchaseMovement
    let productUnitUnbalance = () => {
        return purchaseMovement.sites.map(x => x.movementType == MovementType.PurchaseType
            ? x.volume ?? 0
            : -(x.volume ?? 0)
        ).reduce((prev, curr) => curr = curr + prev, 0)
    }

    let company = vessel.companys[purchaseMovement.companyCode]
    let blockHeaderClass = hasOnlyOneCompany && hasOnlyOneDutyStatus
        ? classes.headerActionAndQuantityFullWidth
        : classes.headerActionAndQuantity

    let products = Object.keys(vessel.products)
        .map((x) => { return { code: vessel.products[x], id: x } })
    let disableSitesSelection = purchaseMovement.sites.some(x => x.movementId != null)
    let remainingQuantity = parseFloat(((purchaseMovement.nominatedQuantity ?? 0) - (purchaseMovement.totalDispatchedQuantity ?? 0)).toFixed(3))
    let dealIds = purchaseMovement.sites.map(site => site.dealId).distinct()
    let site = dealIds.length == 1 ? purchaseMovement.sites.find(x => x.dealId == dealIds.first()) : null
    let dealNumber = dealIds.length > 1 ? '#' : site?.dealNumber?.toString() ?? null
    let purchaseOrder = dealIds.length > 1 ? '#' : site?.purchaseOrder ?? null
    let counterpartyId = purchaseMovement.sites.first()?.counterpartyId ?? vessel.state.counterpartySupplierId
    let counterpartyChoices = (vessel.transporters[purchaseMovement.companyCode] ?? [])
        .filter(x => !x.isInternal)
        .map(x => ({ value: x.id, text: x.name }))


    useEffect(() => {
        if (!!selectedSite && canHaveSeveralMovementTypes) {
            purchaseMovement.sites = purchaseMovement.sites.map(x => ({ ...x, siteCode: selectedSite }))
            vessel.setPurchaseMovement(vesselProduct.id, purchaseMovement)
        }
    }, [selectedSite])

    return <div>
        <div className={classes.purchaseMovementHeader}>
            {hasOnlyOneCompany && hasOnlyOneDutyStatus
                ? undefined
                : <Typography className={classes.purchaseMovementHeaderCombination}
                    variant='h6' display='block'>
                    {hasOnlyOneCompany
                        ? purchaseMovement.dutyStatus
                        : hasOnlyOneDutyStatus
                            ? company
                            : company + " / " + purchaseMovement.dutyStatus}
                </Typography>}
            <div className={blockHeaderClass}>
                {canHaveSeveralMovementTypes
                    ? <div className={classes.quantitiesBottom}>
                        <Typography variant='subtitle2'>{t(tBase + 'site')}</Typography>
                        <Select variant={'standard'}
                            disabled={disableSitesSelection}
                            classesOverride={{ form: classes.purchaseMovementHeaderCombination, select: 'site-select' }}
                            value={selectedSite ?? sites(purchaseMovement)[0]?.value}
                            choices={sites(purchaseMovement)}
                            onChange={val => setSelectedSite(val)} />
                    </div>
                    : undefined}
                {hasVesselDealDrivenFeature() &&
                    <div className={classes.blockSummaries}>
                        <Select label={t(tBase + 'counterparty')}
                            value={counterpartyId}
                            choices={counterpartyChoices}
                            onChange={value =>
                                vessel.changePurchaseMovementCounterparty(vesselProduct.id, purchaseMovement, value)} />
                        <TextField disabled label={t(tBase + 'deal')} text={dealNumber} />
                        <TextField disabled label={t(tBase + 'purchaseOrder')} text={purchaseOrder} />
                    </div>}
                <TotalDispatchedQuantity
                    nominatedQuantity={purchaseMovement.nominatedQuantity}
                    updateNominatedQuantity={v =>
                        vessel.setPurchaseMovement(vesselProduct.id, { ...purchaseMovement, nominatedQuantity: v })}
                    dispatched={purchaseMovement.totalDispatchedQuantity}
                    percentage={purchaseMovement.percentageOfNominated}
                    remaining={remainingQuantity}
                    inAlert={purchaseMovement.inAlert}
                    showRemaining={!canHaveSeveralMovementTypes}
                    remainingLabel={t(tBase + 'remainingQuantity')}
                    showDispatched={!canHaveSeveralMovementTypes}
                    showRemainingPercentage={false}
                    classes={classes} />
                {canHaveSeveralMovementTypes
                    ? <Unbalances
                        mainUnbalance={mainUnbalance()}
                        mainUnit={'TO'}
                        productUnitUnbalance={productUnitUnbalance()}
                        productUnit={vesselProduct.productUnit}
                        classes={classes} />
                    : undefined}
                <div className={classes.purchaseMovementActions}>
                    <Button onClick={() => addNewMovement()}
                        className={`${classes.headerButton} add-site-btn`}>
                        <Add /> {t(tBase + 'addMovement')}
                    </Button>
                    {canDeletePurchaseMovement
                        ? <Button
                            className={`${classes.headerButton} add-site-btn`}
                            onClick={() => setPurchaseMovementToDelete()} >
                            <DeleteOutlined /> {t(tBase + 'deletePurchaseMovement')}
                        </Button>
                        : undefined}
                </div>
            </div>
        </div>
        {canHaveSeveralMovementTypes
            ? <PriceSeveralMovementTypesTable vesselProduct={vesselProduct}
                isFixedPrice={isFixedPrice}
                purchaseMovement={purchaseMovement}
                setPurchaseMovementToDelete={setPurchaseMovementToDelete}
                setSitePurchaseMovementToDelete={setSitePurchaseMovementToDelete}
                canDeletePurchaseMovement={canDeletePurchaseMovement}
                addSite={addSite}
                createSite={createSite}
                duplicateSite={duplicateSite}
                purchaseMovementSite={selectedSite} />
            : <PricePurchaseMovementTable vesselProduct={vesselProduct}
                isFixedPrice={isFixedPrice}
                purchaseMovement={purchaseMovement}
                setPurchaseMovementToDelete={setPurchaseMovementToDelete}
                setSitePurchaseMovementToDelete={setSitePurchaseMovementToDelete}
                canDeletePurchaseMovement={canDeletePurchaseMovement}
                addSite={addSite}
                createSite={createSite}
                duplicateSite={duplicateSite} />}
        <SelectAssignableDeals
            classes={classes}
            setOpenAssignDealPopup={vessel.setOpenAssignDealPopup}
            setDealId={vessel.setDealIdToAssign}
            setIsConfirmDealOpen={vessel.setIsConfirmDealOpen}
            products={products}
            counterpartys={vessel.counterpartys.map(({ name, id }) => ({ name, id }))}
            openAssignDealPopup={vessel.openAssignDealPopup}
            assignableDealsFromMovement={vessel.assignableDealsFromMovement} />
        <ConfirmDealAssignation
            setIsConfirmDealOpen={vessel.setIsConfirmDealOpen}
            isConfirmDealOpen={vessel.isConfirmDealOpen}
            setOpenAssignDealPopup={vessel.setOpenAssignDealPopup}
            setDealIdToAssign={vessel.setDealIdToAssign}
            trySave={vessel.trySave}
            openAssignDealPopup={vessel.openAssignDealPopup}
            assignableDealsFromMovement={vessel.assignableDealsFromMovement}
            assignDealFromMovement={vessel.assignDealFromMovement}
            dealIdToAssign={vessel.dealIdToAssign}
        />
        <CustomDialog
            title={t(tBase + 'unassignDealConfirmDialog.title')}
            contentText={t(tBase + 'unassignDealConfirmDialog.content')}
            isOpen={vessel.unassignDealConfirmDialogOpen}
            onConfirm={async () => {
                await vessel.unassignDeal()
                vessel.setUnassignDealConfirmDialogOpen(false)
            }}
            onCancel={() => vessel.setUnassignDealConfirmDialogOpen(false)}
            onClose={() => vessel.setUnassignDealConfirmDialogOpen(false)}
        />
    </div>
}

export let PriceMovementContainer = withStyles(commonStyle, muiOptions)(_PriceMovementContainer)